// noseFillersData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[];
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const noseFillers: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    "en": {
        "url": "",
        "meta": {
            "title": "Nose Fillers Dubai | Non-Surgical Rhinoplasty",
            "description": "Elite Body Home offers non-surgical nose fillers in Dubai. Achieve a natural, defined look with hyaluronic acid fillers tailored for perfect nose contouring.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/nose-fillers-dubai/",
            "og": {
                "locale": "en_US",
                "type": "article",
                "title": "Nose Fillers Dubai | Non-Surgical Rhinoplasty",
                "description": "Elite Body Home offers non-surgical nose fillers in Dubai. Achieve a natural, defined look with hyaluronic acid fillers tailored for perfect nose contouring.",
                "url": "https://elitebodyhome.com/services/nose-fillers-dubai/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2024-12-30T10:29:57+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-51.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-51.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "Nose Fillers",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "Nose Fillers Dubai | Non-Surgical Rhinoplasty",
                "description": "Elite Body Home offers non-surgical nose fillers in Dubai. Achieve a natural, defined look with hyaluronic acid fillers tailored for perfect nose contouring.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-51.jpg"
            }
        },
        "breadcrumbs": ["Home", "Services", "Nose Fillers Dubai"],
        "hero": {
            "title": "Nose Fillers Treatment in Dubai",
            "subtitle": "Enhance Your Profile with Nose Fillers",
            "description": "Get your dream nose without going under the knife! Elite Body Home offers the nose filler treatment at the very advanced level. Whether you have a bump you want smoothed, a nose shape you want balanced, or a bridge you want lifted, our non-surgical rhinoplasty gives you fast, effective results with little to no downtime. Achieve a sculpted, natural appearance in less than an hour, tailored to your unique facial structure with subtle and aesthetic fill."
        },
        "howItWorks": {
            "title": "Nose Fillers: Sculpt Your Perfect Nose",
            "description": "Nose fillers provide a revolutionary way to achieve a sharper European or elongated nose painlessly within less than an hour with no downtime and immediate results. The procedure, which is performed in an office setting, utilizes high-quality dermal fillers, usually hyaluronic acid, and is injected into specific parts of the nose to soften bumps, raise a flat bridge or correct asymmetry. The process kicks off with a consultation to discuss your goals and a numbing cream application to optimize your comfort level. A skilled practitioner will strategically inject the filler to enhance the appearance of your nose, then gently mold it until it's just right. You'll notice results immediately, and most people can get back to their daily routine in as little as 24 hours. Nose fillers allow you to chisel a more symmetrical and elegant profile without the need of invasive surgery."
        },
        "procedureSteps": {
            "heading": "Nose Filler Procedure – Steps to Follow",
            "para": "",
            "steps": [
                {
                    "title": "Consultation",
                    "desc": "The practitioner also assesses the patient's nose, goes over expectations and decides whether or not fillers are appropriate, based on anatomy and goals.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "Preparation",
                    "desc": "The nose and surrounding areas are cleansed in detail, and a topical anesthetic cream is put on to reduce discomfort.",
                    "icon": "fa-solid fa-prescription-bottle-medical"
                },
                {
                    "title": "Marking the Injection Points",
                    "desc": "The physician marks the areas for injecting the filler with precision to shape and contour.",
                    "icon": "fa-solid fa-map-marker-alt"
                },
                {
                    "title": "Filler Injection",
                    "desc": "A fine needle or cannula is used to inject the filler into targeted areas of the nose (such as the bridge, tip, or nostrils) that the person wants to enhance, depending on the desired treatment.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "Shaping and Sculpting",
                    "desc": "The practitioner then massages and shapes the filler to create the desired contour and symmetry.",
                    "icon": "fa-solid fa-hands"
                },
                {
                    "title": "Evaluation and Adjustments",
                    "desc": "Then after the results are evaluated, more filler can be injected for balance and symmetry.",
                    "icon": "fa-solid fa-check-circle"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits",
            "subheading": "",
            "para": "Nose fillers, known as the non-surgical rhinoplasty, have gained massive popularity because it helps enhance and reshape the nose without the need for invasive surgery. Ideal for someone that is looking to remove small things on their nose or improve their nose symmetry. Nose fillers are a fully quick and non-invasive procedure that comes with minimal downtime and jaw-dropping results, unlike traditional rhinoplasty.",
            "items": [
                {
                    "title": "Non-Invasive Procedure",
                    "desc": "Injectable nose fillers do not require surgery or anesthesia.",
                    "icon": "fa-solid fa-hand-holding-medical"
                },
                {
                    "title": "Quick Results",
                    "desc": "The whole procedure usually lasts under 30 minutes with results visible right after.",
                    "icon": "fa-solid fa-stopwatch"
                },
                {
                    "title": "Minimal Downtime",
                    "desc": "Patients can resume normal activities almost immediately, needing little to no recovery time.",
                    "icon": "fa-solid fa-bed"
                },
                {
                    "title": "Customizable Adjustments",
                    "desc": "As the procedure allows for precise, subtle changes, it is easy to tailor the result to the patient's liking.",
                    "icon": "fa-solid fa-sliders-h"
                },
                {
                    "title": "Cost-Effective",
                    "desc": "Nose fillers are less expensive than surgical rhinoplasty for reshaping the nose.",
                    "icon": "fa-solid fa-money-bill-wave"
                },
                {
                    "title": "Natural-Looking Results",
                    "desc": "Fillers, when placed properly, can create subtle, natural enhancements that are in harmony with the patient's facial features.",
                    "icon": "fa-solid fa-face-smile"
                }
            ]
        },
        "eligibility": {
            "heading": "Is Nose Fillers Right for You? Eligibility Explained",
            "subheading": "",
            "para": "If you are looking for non-surgical enhancements to your nose, nose fillers may be the right way for you to go. Although the treatment provides flexibility and convenience, you want to know if you make a good candidate.",
            "items": [
                {
                    "title": "When Nose Filler Treatment is Appropriate",
                    "desc": "Great to soften small bumps, asymmetry or depressions on the nose.",
                    "icon": "fa-solid fa-check-circle"
                },
                {
                    "title": "Temporary Solution Seekers",
                    "desc": "Fillers offer a non-permanent option for sculpting the nose; therefore, it is a great option for individuals who want to see if they like the result before truly committing to change.",
                    "icon": "fa-solid fa-clock-rotate-left"
                },
                {
                    "title": "Patients Avoiding Surgery",
                    "desc": "Nose fillers are ideal if you're looking for a non-invasive option to rhinoplasty, as they are safe and quick.",
                    "icon": "fa-solid fa-procedures"
                },
                {
                    "title": "People Looking for Natural-Looking Improvements",
                    "desc": "Nose fillers are the ideal cosmetic procedure for those who want a subtle change without significantly altering their facial structure.",
                    "icon": "fa-solid fa-face-grin"
                },
                {
                    "title": "Anyone Without Any Major Nose Problems",
                    "desc": "Nose fillers are effective in cosmetic adjustments but aren't not good for significant structural or breathing problems.",
                    "icon": "fa-solid fa-ban"
                }
            ]
        },
        "preparationTips": {
            "heading": "How to Prepare for Nose Filler Procedure",
            "subheading": "",
            "para": "A nose filler treatment can lead to amazing results, but preparation is key to ensuring the treatment goes smoothly and achieves the best possible outcomes. Correct preparation minimizes the changes of complications and increases the treatment effect — if you are not well prepared for the procedure, the results might not be optimal. Nose fillers are minimally invasive, but there are some important nose filler tips to follow before your appointment, and they can make a huge difference in your experience as a whole.",
            "items": [
                {
                    "title": "No Food With Blood Thinner Meds",
                    "desc": "For at least a week before this procedure, avoid aspirin, ibuprofen and other blood-thinning medications that can heighten your risk of bruising.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "Limit Alcohol and Smoking",
                    "desc": "Alcohol and smoking can slow healing and increase the chance of swelling. It's suggested to stay away from them for at least 48 hours before the treatment.",
                    "icon": "fa-solid fa-wine-bottle"
                },
                {
                    "title": "Stay Hydrated",
                    "desc": "Drinking enough water helps you stay hydrated, which can help fillers work better and heal faster.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "Consult with Your Doctor",
                    "desc": "Tell your practitioner about any allergies, medications or past medical problems. This enables them to customize the treatment to your particular situation and safeguard your well-being.",
                    "icon": "fa-solid fa-user-doctor"
                },
                {
                    "title": "Plan Ahead",
                    "desc": "Because minor swelling or bruising may occur after treatment, it's wise to schedule your appointment at least one week before any important events.",
                    "icon": "fa-solid fa-calendar-check"
                }
            ]
        },
        "results": {
            "heading": "Results",
            "subheading": "",
            "para": "Nose filler is an incredible non-surgical option for those seeking to improve angulation and patient satisfaction peaks, with instant results, little downtime. The process is simple, but knowing the potential outcomes can help you prepare, both in terms of expectations and benefits from the treatment. Here's what to expect after you've had nose filler:",
            "items": [
                {
                    "title": "Instant Results",
                    "desc": "You'll see a marked difference right after the procedure, with your nose looking more symmetrical and contoured.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "Subtle Enhancements",
                    "desc": "Fillers give you natural-looking results that improve the shape of your nose without looking overdone.",
                    "icon": "fa-solid fa-face-grin-stars"
                },
                {
                    "title": "Temporary Effects",
                    "desc": "The effects usually last around 6-18 months depending on the filler.",
                    "icon": "fa-solid fa-hourglass-half"
                },
                {
                    "title": "Minimal Downtime",
                    "desc": "You can usually go back to most daily activities immediately following the procedure, and there's little to no recovery period.",
                    "icon": "fa-solid fa-couch"
                }
            ]
        },
        "aftercareTips": {
            "heading": "Aftercare for Nose Filler",
            "subheading": "",
            "para": "So, post-nose filler aftercare is imperative for the best possible results and minimizing side effects. Although the procedure is non-invasive, you can aid your healing and longer-lasting results by following some simple aftercare guidelines. Some post-care tips to follow are:",
            "items": [
                {
                    "title": "Refrain from Touching or Putting Pressure on the Area",
                    "desc": "Do not touch, massage or apply pressure to the area for a minimum of 24-48 hours post treatment in order to avoid displacement of filler.",
                    "icon": "fa-solid fa-hand-paper"
                },
                {
                    "title": "Limit Sun Exposure",
                    "desc": "Swelling or affecting the longevity of the filler can happen when the filler is exposed to direct sun. Put on sunscreen and limit sun exposure for a few days following treatment.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "Skip Strenuous Activities",
                    "desc": "If you need to, avoid heavy exercises or allowed excessive sweating for almost one or two days so you would not bother your skin structure.",
                    "icon": "fa-solid fa-dumbbell"
                },
                {
                    "title": "Stay Hydrated",
                    "desc": "Staying well-hydrated by drinking lots of water will also help to keep your skin hydrated and support healing, which may improve the end result of the filler.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "Apply Ice Packs for Swelling",
                    "desc": "If you have minor swelling or bruises, a cold compress will also help reduce inflammation. Do remember to put a cloth between the ice and skin.",
                    "icon": "fa-solid fa-snowflake"
                },
                {
                    "title": "Avoid Alcohol and Smoking",
                    "desc": "Both can delay recovery and lead to more swelling or bruising, so it's best to steer clear of them for at least 48 hours after the procedure.",
                    "icon": "fa-solid fa-ban-smoking"
                }
            ]
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "Everything you need to know about nose fillers and what to expect from your treatment.",
            "items": [
                {
                    "question": "How long do nose fillers last?",
                    "answer": "Nose fillers typically last between 6 to 18 months, depending on the type of filler used and individual factors."
                },
                {
                    "question": "Do nose fillers permanently change the nose shape?",
                    "answer": "No, nose fillers provide temporary results. The effects gradually wear off as the filler is absorbed by the body."
                },
                {
                    "question": "Are nose fillers painful?",
                    "answer": "Most patients report minimal discomfort. A numbing cream is applied before the procedure to maximize comfort."
                },
                {
                    "question": "What is the downtime after nose fillers?",
                    "answer": "There is minimal downtime. You may experience mild swelling or bruising that typically resolves within a few days."
                },
                {
                    "question": "Can nose fillers fix breathing problems?",
                    "answer": "No, nose fillers are purely cosmetic and cannot correct structural issues affecting breathing."
                }
            ]
        },
        "treatmentAreas1": [
        ],
        "treatmentAreas2": [
        ]
    },
    "shared": {
        "images": {
            "introductionImage": "",
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-51.jpg",
            "beforeAfter": [
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/rtuytjyt-1.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/rtuytjyt-2.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/rtuytjyt-3.jpg"
            ]
        },
        "product": {
            "id": 112,
            "title": "Nose Filler Treatment (Non-Surgical Rhinoplasty)",
            "price": 499,
            "description": "Enhance and reshape your nose without surgery using our expertly applied hyaluronic acid nose fillers. Achieve symmetry and contour in under 30 minutes."
        },
    },

    "ar": {
        "url": "",
        "meta": {
            "title": "فيلر الأنف في دبي | تجميل الأنف غير الجراحي",
            "description": "تقدم عيادات ايليت بودي هوم فيلر الأنف غير الجراحي في دبي. احصل على مظهر طبيعي ومحدد باستخدام فيلر حمض الهيالورونيك لتحقيق تناسق مثالي للأنف.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/nose-fillers-dubai",
            "og": {
                "locale": "ar_AR",
                "type": "article",
                "title": "فيلر الأنف في دبي | تجميل الأنف غير الجراحي",
                "description": "تقدم عيادات ايليت بودي هوم فيلر الأنف غير الجراحي في دبي. احصل على مظهر طبيعي ومحدد باستخدام فيلر حمض الهيالورونيك لتحقيق تناسق مثالي للأنف.",
                "url": "https://elitebodyhome.com/services/nose-fillers-dubai",
                "site_name": "elitebodyhome.com",
                "updated_time": "2024-12-30T10:29:57+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-51.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-51.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "فيلر الأنف",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "فيلر الأنف في دبي | تجميل الأنف غير الجراحي",
                "description": "تقدم عيادات ايليت بودي هوم فيلر الأنف غير الجراحي في دبي. احصل على مظهر طبيعي ومحدد باستخدام فيلر حمض الهيالورونيك لتحقيق تناسق مثالي للأنف.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-51.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "فيلر الأنف في دبي"],
        "hero": {
            "title": "علاج فيلر الأنف في دبي",
            "subtitle": "حسّن ملامح وجهك مع فيلر الأنف",
            "description": "احصل على ملامح الأنف التي تحلم بها دون الحاجة إلى الجراحة! توفر لك عيادات ايليت بودي هوم علاج فيلر الأنف بأحدث التقنيات. سواء كنت ترغب في التخلص من نتوءات الأنف، أو تحقيق توازن في شكل الأنف، أو رفع عظام الجسر، سيمنحك تجميل الأنف غير الجراحي لدينا نتائج سريعة وفعّالة مع فترة التعافي قصيرة أو دون الحاجة إليها. احصل على مظهر متناسق وطبيعي في أقل من ساعة، مع إجراء دقيق مخصص ليتناسب مع ملامح وجهك بأسلوب جمالي."
        },
        "howItWorks": {
            "title": "فيلر الأنف: نحت مثالي لملامح الأنف",
            "description": "يوفر فيلر الأنف طريقة متطورة للحصول على أنف أوروبية أكثر تحديدًا أو أنف ممدودة دون ألم وفي أقل من ساعة، مع نتائج فورية ودون الحاجة إلى فترة تعافي. يتم الإجراء في العيادة باستخدام فيلر جلدي عالي الجودة مكون من حمض الهيالورونيك، يُحقن في مناطق محددة من الأنف لتنعيم النتوءات، أو رفع الجسر المسطح، أو تصحيح عدم التماثل. يبدأ الإجراء بجلسة استشارية لمناقشة أهدافك، يليها استخدام كريم مخدر لضمان أقصى درجات الراحة. يقوم الطبيب المتخصص بعد ذلك بحقن الفيلر بدقة في المناطق المستهدفة لتحسين شكل الأنف، مع إعادة تشكيله بلطف لتحقيق التناسق المثالي. يمكنك ملاحظة النتائج فوراً بعد الإجراء، كما يتمكن معظم الأشخاص من استئناف أنشطتهم اليومية خلال 24 ساعة فقط. يمنحك فيلر الأنف فرصة لنحت مظهر أكثر تناسقًا وأناقًة، دون الحاجة إلى أي تدخل جراحي."
        },
        "procedureSteps": {
            "heading": "خطوات إجراء فيلر الأنف",
            "para": "",
            "steps": [
                {
                    "title": "الاستشارة",
                    "desc": "يقوم الطبيب بتقييم شكل الأنف ومناقشة التوقعات، وتحديد ما إذا كان الفيلر إجراءً ملائمًا لك أم لا بناءً على بنية الأنف وأهدافك.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "الاستعداد",
                    "desc": "يًنظف الأنف والمناطق المحيطة به بعناية، ثم يُستخدم كريم مخدر موضعي لتقليل الشعور بالألم.",
                    "icon": "fa-solid fa-prescription-bottle-medical"
                },
                {
                    "title": "تحديد نقاط الحقن",
                    "desc": "يقوم الطبيب المعالج بوضع علامات دقيقة على مواقع الحقن لتشكيل الأنف وتحديده بالشكل المطلوب.",
                    "icon": "fa-solid fa-map-marker-alt"
                },
                {
                    "title": "حقن الفيلر",
                    "desc": "تُستخدم إبرة أو قنية دقيقة لحقن الفيلر في المناطق المستهدفة مثل جسر الأنف أو طرفه أو فتحات الأنف، حسب احتياجات المريض.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "التشكيل والنحت",
                    "desc": "يقوم الطبيب المتخصص بتدليك الفيلر وتشكيله بلطف للحصول على تناسق مثالي وشكل متوازن.",
                    "icon": "fa-solid fa-hands"
                },
                {
                    "title": "التقييم والتعديل",
                    "desc": "يتم تقييم النتائج فورًا، يمكن إضافة المزيد من الفيلر إذا لزم الأمر لضبط التناسق وتحقيق المظهر المطلوب.",
                    "icon": "fa-solid fa-check-circle"
                }
            ]
        },
        "benefits": {
            "heading": "مزايا فيلر الأنف",
            "subheading": "",
            "para": "يُعرف فيلر الأنف أيضًا بتجميل الأنف غير الجراحي، وقد اكتسب شعبية كبيرة بفضل قدرته على تحسين شكل الأنف وإعادة تشكيله دون الحاجة إلى الجراحة. يعد إجراء مثالي لمن يرغب في تصحيح العيوب الطفيفة في الأنف أو تحسين تناسقه، حيث إنه إجراء سريع وغير جراحي تمامًا، يوفر نتائج مذهلة مع الحد الأدنى من فترة التعافي، على عكس عملية تجميل الأنف التقليدية.",
            "items": [
                {
                    "title": "إجراء غير جراحي",
                    "desc": "لا يتطلب فيلر الأنف جراحة أو تخديرًا كليًا.",
                    "icon": "fa-solid fa-hand-holding-medical"
                },
                {
                    "title": "نتائج سريعة",
                    "desc": "يستغرق الإجراء أقل من 30 دقيقة، وتظهر النتائج على الفور.",
                    "icon": "fa-solid fa-stopwatch"
                },
                {
                    "title": "فترة تعافي قصيرة",
                    "desc": "يمكن للمرضى استئناف أنشطتهم اليومية مباشرةً بعد الجلسة مع فترة تعافي قصيرة أو دون الحاجة إليها.",
                    "icon": "fa-solid fa-bed"
                },
                {
                    "title": "تعديلات مخصصة لك",
                    "desc": "يتيح الفيلر إجراء تغييرات دقيقة وطفيفة، مما يسمح بتعديل النتائج وفقًا لرغبة المريض.",
                    "icon": "fa-solid fa-sliders-h"
                },
                {
                    "title": "تكلفة مناسبة",
                    "desc": "يُعد فيلر الأنف خيارًا أقل تكلفة مقارنة بجراحة تجميل الأنف التقليدية.",
                    "icon": "fa-solid fa-money-bill-wave"
                },
                {
                    "title": "نتائج طبيعية",
                    "desc": "يمنح الفيلر تحسينات دقيقة تتناسب مع ملامح الوجه عند تطبيقه بشكل صحيح، مما يوفر مظهراً طبيعياً ومتناسقاً.",
                    "icon": "fa-solid fa-face-smile"
                }
            ]
        },
        "eligibility": {
            "heading": "هل فيلر الأنف مناسب لك؟ معايير الأهلية",
            "subheading": "",
            "para": "إذا كنت تبحث عن تعديلات غير جراحية لشكل الأنف، فقد يكون فيلر الأنف خيار مثالي لك. ورغم أن هذا الإجراء يوفر مرونة وراحة كبيرة، فمن المهم معرفة ما إذا كنت مرشحًا مناسبًا له.",
            "items": [
                {
                    "title": "الأشخاص الذين يرغبون في تصحيح النتوءات أو المنخفضات الصغيرة في الأنف، أو عدم التماثل",
                    "desc": "مثالي لتخفيف النتوءات الصغيرة أو عدم التناسق أو المنخفضات في الأنف.",
                    "icon": "fa-solid fa-check-circle"
                },
                {
                    "title": "الأشخاص الذين يرغبون في حلول مؤقتة",
                    "desc": "يوفر الفيلر خيارًا مؤقت لإعادة تشكيل الأنف، مما يجعله مثاليًا لمن يرغب في تجربة التغيير قبل اتخاذ قرار دائم.",
                    "icon": "fa-solid fa-clock-rotate-left"
                },
                {
                    "title": "المرضى الذين يفضّلون تجنب العمليات الجراحية",
                    "desc": "يعد الفيلر خيارًا آمنًا وسريعًا لمن يبحث عن بديل غير جراحي لعملية تجميل الأنف.",
                    "icon": "fa-solid fa-procedures"
                },
                {
                    "title": "الأشخاص الذين يسعون إلى تحسينات طبيعية",
                    "desc": "يناسب من يرغب في إجراء تعديل بسيط يعزز تناسق ملامح الوجه دون تغيير جذري في بنيته.",
                    "icon": "fa-solid fa-face-grin"
                },
                {
                    "title": "الأشخاص الذين لا يعانون من مشكلات كبيرة في الأنف",
                    "desc": "يعد الفيلر فعال للتعديلات التجميلية، لكنه لا يصلح لحالات التشوهات الهيكلية أو مشكلات التنفس.",
                    "icon": "fa-solid fa-ban"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد",
            "subheading": "",
            "para": "قد ينتج عن فيلر الأنف نتائج مذهلة، يعد التحضير الجيد أمرًا أساسيًا لضمان تجربة علاج سلسة والحصول على أفضل نتائج ممكنة. يضمن التحضير السليم تقليل احتمالية حدوث مضاعفات وزيادة فعالية العلاج. إذا لم تكن مستعدًا بشكل جيد للعملية، فقد لا تحصل على نتائج مثالية. يعتبر فيلر الأنف إجراء غير جراحي، ولكن يوجد بعض النصائح الهامة التي يجب اتباعها قبل موعد جلستك، ويمكن أن تُحدث فارقًا كبيرًا في تجربتك بشكل عام، ومنها:",
            "items": [
                {
                    "title": "تجنب الأدوية والأطعمة المُميعة للدم",
                    "desc": "يجب تجنب تناول الأسبرين والإيبوبروفين وأي أدوية مميعة للدم لمدة أسبوع على الأقل قبل إجراء الحقن، حيث يمكن أن ترفع من خطر حدوث كدمات.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "الامتناع عن الكحول والتدخين",
                    "desc": "يمكن أن يبطئ الكحول والتدخين من عملية الشفاء ويرفعان من فرصة التورم. يُفضل الابتعاد عنهم لمدة 48 ساعة على الأقل قبل العلاج.",
                    "icon": "fa-solid fa-wine-bottle"
                },
                {
                    "title": "الحفاظ على ترطيب الجسم",
                    "desc": "يساعد شرب كمية كافية من الماء في الحفاظ على ترطيب الجسم، مما يساعد الفيلر على العمل بشكل أفضل ويعزز الشفاء بسرعة أكبر.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "استشر طبيبك",
                    "desc": "أخبر الطبيب المعالج إذا كنت تعاني من حساسية أو مشكلات طبية سابقة أو إذا كنت تتناول أي أدوية. سيساعدهم ذلك في تخصيص العلاج وفقًا لحالتك وضمان سلامتك.",
                    "icon": "fa-solid fa-user-doctor"
                },
                {
                    "title": "خطط مسبقًا",
                    "desc": "يُفضل تحديد موعد الجلسة قبل أي مناسبة مهمة بمدة لا تقل عن أسبوع، نظرًا لاحتمال حدوث تورم طفيف أو كدمات بعد العلاج.",
                    "icon": "fa-solid fa-calendar-check"
                }
            ]
        },
        "results": {
            "heading": "نتائج فيلر الانف",
            "subheading": "",
            "para": "يعد فيلر الأنف خيار غير جراحي مذهل لتحسين ميلان الأنف وزيادة رضا المرضى، حيث يوفر نتائج فورية ووقت تعافي قصير. العملية بسيطة، ولكن يمكن لمعرفة النتائج المحتملة مساعدتك على الاستعداد من حيث التوقعات أو الفوائد التي يمكن تحقيقها من العلاج. إليك ما يمكنك توقعه بعد جلسة فيلر الأنف:",
            "items": [
                {
                    "title": "نتائج فورية",
                    "desc": "ستلاحظ تحسنًا واضحًا بعد الإجراء مباشرةً، حيث سيبدو أنفك أكثر تناسقًا وتحديدًا.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "تحسينات طبيعية",
                    "desc": "يمنحك الفيلر مظهرًا متناسقًا دون مبالغة، مما يعزز شكل الأنف بشكل طبيعي.",
                    "icon": "fa-solid fa-face-grin-stars"
                },
                {
                    "title": "نتائج مؤقتة",
                    "desc": "تدوم النتائج عادةً ما بين 6 إلى 18 شهرًا، حسب نوع الفيلر المستخدم.",
                    "icon": "fa-solid fa-hourglass-half"
                },
                {
                    "title": "لا يتطلب فترة تعافي",
                    "desc": "يمكنك العودة إلى معظم أنشطتك اليومية فورًا بعد الجلسة،حيث إنه لا يتطلب فترة تعافي طويلة.",
                    "icon": "fa-solid fa-couch"
                }
            ]
        },
        "aftercareTips": {
            "heading": "نصائح العناية بعد فيلر الأنف",
            "subheading": "",
            "para": "تُعدّ العناية بعد العلاج أمرًا ضروريًا للحصول على أفضل النتائج وتقليل الآثار الجانبية. وعلى الرغم من أن العلاج غير جراحي، إلا أن اتباع بعض الإرشادات البسيطة يمكن أن يساعد في تسريع عملية الشفاء والحفاظ على النتائج لفترة أطول. بعض النصائح الهامة للعناية بعد العلاج:",
            "items": [
                {
                    "title": "تجنب لمس أو الضغط على المنطقة المعالجة",
                    "desc": "لا تلمس أو تدلك أو تضغط على الأنف لمدة 24-48 ساعة على الأقل بعد العلاج، لتجنب تحرك الفيلر من موضعه.",
                    "icon": "fa-solid fa-hand-paper"
                },
                {
                    "title": "تجنب التعرض لأشعة الشمس",
                    "desc": "قد يؤدي التعرض المباشر للشمس إلى زيادة التورم أو التأثير على مدة بقاء الفيلر. احرص على وضع واقي الشمس وتجنب أشعة الشمس المباشرة لبضعة أيام بعد العلاج.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "تجنب الأنشطة المجهدة",
                    "desc": "يُفضَّل الامتناع عن التمارين الرياضية الشاقة أو التعرق المفرط لمدة يوم أو يومين حتى لا تؤثر على بنية الجلد.",
                    "icon": "fa-solid fa-dumbbell"
                },
                {
                    "title": "الحفاظ على الترطيب",
                    "desc": "يساعد شرب كميات كافية من الماء على ترطيب البشرة وتعزيز عملية التعافي، مما قد يساهم في تحسين النتائج النهائية.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "استخدام كمادات باردة للتورم",
                    "desc": "إذا كنت تعاني من كدمات أو تورم طفيف، يمكن لاستخدام كمادات باردة أن يساعد في تقليل الالتهاب، مع الحرص على وضع قطعة قماش بين الثلج والجلد.",
                    "icon": "fa-solid fa-snowflake"
                },
                {
                    "title": "الامتناع عن الكحول والتدخين",
                    "desc": "يؤثر كلاهما على سرعة التعافي و يرفع استهلاكهم من احتمالية حدوث تورم أو كدمات، لذا من الأفضل تجنبهما لمدة لا تقل عن 48 ساعة بعد الإجراء.",
                    "icon": "fa-solid fa-ban-smoking"
                }
            ]
        },
        "faqData": {
            "heading": "الأسئلة المكررة",
            "subheading": "كل ما تحتاج معرفته عن فيلر الأنف وما يمكن توقعه من العلاج.",
            "items": [
                {
                    "question": "كم تدوم نتائج فيلر الأنف؟",
                    "answer": "عادةً ما تدوم نتائج فيلر الأنف بين 6 إلى 18 شهرًا، حسب نوع الفيلر المستخدم والعوامل الفردية."
                },
                {
                    "question": "هل يغير فيلر الأنف شكل الأنف بشكل دائم؟",
                    "answer": "لا، يوفر فيلر الأنف نتائج مؤقتة. تتلاشى التأثيرات تدريجياً مع امتصاص الجسم للفيلر."
                },
                {
                    "question": "هل فيلر الأنف مؤلم؟",
                    "answer": "معظم المرضى يبلغون عن انزعاج بسيط. يتم تطبيق كريم مخدر قبل الإجراء لضمان أقصى درجات الراحة."
                },
                {
                    "question": "ما هي فترة التعافي بعد فيلر الأنف؟",
                    "answer": "فترة التعافي قصيرة جدًا. قد تواجه تورمًا أو كدمات طفيفة تختفي عادةً خلال بضعة أيام."
                },
                {
                    "question": "هل يمكن لفيلر الأنف علاج مشاكل التنفس؟",
                    "answer": "لا، فيلر الأنف هو إجراء تجميلي بحت ولا يمكنه تصحيح المشاكل الهيكلية التي تؤثر على التنفس."
                }
            ]
        },
        "treatmentAreas1": [

        ],
        "treatmentAreas2": [

        ]

    }
};
